// Smooth moves of board cards (FLIP): after each render a card that changed its place in its column slides there from the old one,
// and a dropped card lands from where the ghost was let go (or from its old place after a keyboard move). Positions are layout offsets, so running animations and scroll do not skew them.

import { useLayoutEffect, useRef } from 'react';

export const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** The motion tokens of DESIGN.md «Движение» and the pose of a lifted card, read from the element (no numbers of its own). */
export function motion(el: HTMLElement) {
  const cs = getComputedStyle(el);
  const v = (name: string) => cs.getPropertyValue(name).trim();
  return {
    dur: parseFloat(v('--dur')) || 0, ease: v('--ease') || 'ease', spring: v('--ease-spring') || 'ease',
    lift: `rotate(${v('--lift-tilt') || '0deg'}) scale(${v('--lift-scale') || '1'})`,
    high: v('--shadow-lg') || 'none', low: v('--shadow-sm') || 'none',
  };
}

/**
 * Animates the cards of `root` ([data-id] inside [data-cards]) on every render where `key` changed. `drop` is the rectangle
 * of the released ghost: the card with that id slides from it into its new place until `until`.
 */
export function useFlip(root: React.RefObject<HTMLElement | null>, key: string, drop: React.RefObject<{ id: string; rect: DOMRect; until: number } | null>) {
  const prev = useRef(new Map<string, { col: string; top: number }>());
  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return;
    const next = new Map<string, { col: string; top: number }>();
    const skip = reducedMotion();
    const m = skip ? null : motion(el);
    const t = m && { duration: m.dur, easing: m.spring };
    for (const card of el.querySelectorAll<HTMLElement>('[data-cards] > [data-id]')) {
      const id = card.dataset.id!;
      const col = (card.parentElement as HTMLElement).dataset.cards!;
      const top = card.offsetTop;
      next.set(id, { col, top });
      if (!t) continue;
      const was = prev.current.get(id);
      const d = drop.current;
      // The store may confirm the move a render later than the ghost goes: the card slides from the ghost on each new place it takes.
      if (d && d.id === id && performance.now() < d.until) {
        if (!was || was.col !== col || was.top !== top) {
          card.getAnimations().forEach((a) => a.cancel());
          // The rectangle is the tilted card of the ghost, so its box is wider than the card: the landing goes centre to centre.
          const r = card.getBoundingClientRect();
          const x = d.rect.left + d.rect.width / 2 - (r.left + r.width / 2), y = d.rect.top + d.rect.height / 2 - (r.top + r.height / 2);
          card.animate([
            { transform: `translate(${x}px, ${y}px) ${m!.lift}`, boxShadow: m!.high, zIndex: 3 },
            { transform: 'none', boxShadow: m!.low, zIndex: 3 },
          ], t);
        }
        continue;
      }
      if (was && was.col === col && was.top !== top) {
        // A move that interrupts a running one starts from where the card is seen now, not from its old layout place.
        const running = card.getAnimations();
        const tf = running.length ? getComputedStyle(card).transform : 'none';
        const seen = tf && tf !== 'none' ? new DOMMatrixReadOnly(tf).m42 : 0;
        running.forEach((a) => a.cancel());
        card.animate([{ transform: `translateY(${was.top + seen - top}px)` }, { transform: 'none' }], t);
      }
    }
    if (drop.current && performance.now() >= drop.current.until) drop.current = null;
    prev.current = next;
  }, [key]);
}
